import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Image, ProgressBar, Badge } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { FaUpload, FaTrash, FaCompress } from 'react-icons/fa';
import { agentService } from '../services/api';
import { prepareImageForUpload } from '../utils/imageCompression';
import { getStaticFileUrl } from '../config/api';

const PhotoUploadModal = ({ show, onHide, agent }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const queryClient = useQueryClient();

  // Mutations
  const uploadMutation = useMutation(
    ({ id, formData }) => agentService.uploadPhoto(id, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agents');
        handleClose();
      }
    }
  );

  const deleteMutation = useMutation(agentService.deletePhoto, {
    onSuccess: () => {
      queryClient.invalidateQueries('agents');
      handleClose();
    }
  });

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide.');
        return;
      }

      setIsCompressing(true);
      setCompressionInfo(null);

      // Préparer et compresser l'image si nécessaire
      const { file: processedFile, isCompressed, originalSize, finalSize } = await prepareImageForUpload(file);
      
      setSelectedFile(processedFile);
      setCompressionInfo({
        isCompressed,
        originalSize,
        finalSize,
        compressionRatio: isCompressed ? ((originalSize - finalSize) / originalSize * 100).toFixed(1) : 0
      });
      
      // Créer une prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(processedFile);

    } catch (error) {
      console.error('Erreur lors de la préparation de l\'image:', error);
      alert('Erreur lors de la préparation de l\'image: ' + error.message);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !agent) return;

    const formData = new FormData();
    formData.append('photo', selectedFile);

    try {
      await uploadMutation.mutateAsync({ id: agent.id, formData });
    } catch (error) {
      console.error('Erreur upload:', error);
    }
  };

  const handleDeletePhoto = async () => {
    if (!agent || !agent.photoPath) return;

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      try {
        await deleteMutation.mutateAsync(agent.id);
      } catch (error) {
        console.error('Erreur suppression:', error);
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setCompressionInfo(null);
    onHide();
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    // Utiliser la configuration centralisée pour obtenir l'URL correcte
    const filename = photoPath.split('/').pop();
    return getStaticFileUrl(filename);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const currentPhotoUrl = agent?.photoPath ? getPhotoUrl(agent.photoPath) : null;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Gérer la photo - {agent?.nom} {agent?.prenoms}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(uploadMutation.error || deleteMutation.error) && (
          <Alert variant="danger">
            Erreur: {uploadMutation.error?.response?.data || deleteMutation.error?.response?.data || 
                    uploadMutation.error?.message || deleteMutation.error?.message}
          </Alert>
        )}

        <div className="row">
          {/* Photo actuelle */}
          <div className="col-md-6">
            <h6>Photo actuelle</h6>
            {currentPhotoUrl ? (
              <div className="text-center">
                <Image 
                  src={currentPhotoUrl} 
                  alt={`${agent?.nom} ${agent?.prenoms}`}
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                  className="border rounded mb-3"
                />
                <div>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={handleDeletePhoto}
                    disabled={deleteMutation.isLoading}
                  >
                    <FaTrash className="me-1" />
                    {deleteMutation.isLoading ? 'Suppression...' : 'Supprimer'}
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className="border rounded d-flex align-items-center justify-content-center"
                style={{ height: '200px', backgroundColor: '#f8f9fa' }}
              >
                <span className="text-muted">Aucune photo</span>
              </div>
            )}
          </div>

          {/* Nouvelle photo */}
          <div className="col-md-6">
            <h6>Nouvelle photo</h6>
            <Form.Group className="mb-3">
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={isCompressing}
              />
              <Form.Text className="text-muted">
                Formats acceptés: JPG, PNG, GIF. Les images seront automatiquement compressées si nécessaire.
              </Form.Text>
            </Form.Group>

            {/* Indicateur de compression */}
            {isCompressing && (
              <div className="mb-3">
                <div className="d-flex align-items-center">
                  <FaCompress className="me-2 text-primary" />
                  <span>Compression en cours...</span>
                </div>
                <ProgressBar animated now={100} className="mt-2" />
              </div>
            )}

            {/* Information de compression */}
            {compressionInfo && (
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-2">
                  {compressionInfo.isCompressed ? (
                    <>
                      <Badge bg="success">
                        <FaCompress className="me-1" />
                        Compressée (-{compressionInfo.compressionRatio}%)
                      </Badge>
                      <Badge bg="info">
                        {formatFileSize(compressionInfo.originalSize)} → {formatFileSize(compressionInfo.finalSize)}
                      </Badge>
                    </>
                  ) : (
                    <Badge bg="secondary">
                      Aucune compression nécessaire ({formatFileSize(compressionInfo.finalSize)})
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {preview && (
              <div className="text-center">
                <Image 
                  src={preview} 
                  alt="Prévisualisation"
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                  className="border rounded mb-3"
                />
                <div>
                  <Button 
                    variant="primary"
                    onClick={handleUpload}
                    disabled={uploadMutation.isLoading || isCompressing}
                  >
                    <FaUpload className="me-1" />
                    {uploadMutation.isLoading ? 'Upload...' : 'Uploader'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PhotoUploadModal; 